import { formatDate } from './utils';

export enum LogMethod {
  TRACE = 'trace',
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

const LogLevel: { [name in LogMethod]: number } = {
  [LogMethod.TRACE]: 0,
  [LogMethod.DEBUG]: 1,
  [LogMethod.INFO]: 2,
  [LogMethod.WARN]: 3,
  [LogMethod.ERROR]: 4,
};

class Logger {
  private static maxCount: number = 100000;
  private static discardStep: number = 10000;
  private static logLevel: number = 2;
  private tag: string;

  static logs: Array<any> = [];

  static setConfig(maxCount: number = 100000, discardStep: number = 10000) {
    if (maxCount !== Logger.maxCount && discardStep > 0) {
      Logger.maxCount = Math.floor(maxCount);
      Logger.discardStep = Math.floor(discardStep);
    }
  }

  constructor(tag: string = '') {
    this.tag = tag;
  }

  get logList() {
    return Logger.logs;
  }

  private get logTimeTag() {
    return `[${formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss')}]`;
  }

  public setLevel(level: LogMethod) {
    if (level && LogLevel[level] !== undefined) {
      Logger.logLevel = LogLevel[level];
    }
  }

  trace(...args: any[]) {
    this.l(LogMethod.TRACE, ...args);
  }

  debug(...args: any[]) {
    this.l(LogMethod.DEBUG, ...args);
  }

  info(...args: any[]) {
    this.l(LogMethod.INFO, ...args);
  }

  warn(...args: any[]) {
    this.l(LogMethod.WARN, ...args);
  }

  error(...args: any[]) {
    this.l(LogMethod.ERROR, ...args);
  }

  private pushLog(args: any[]) {
    if (Logger.logs.length > Logger.maxCount) {
      Logger.logs.splice(0, Logger.discardStep);
    }
    Logger.logs.push(`${args.join(' ')}\n`);
  }

  private l(logLevel: LogMethod, ...args: any[]) {
    if (this.tag) {
      args.unshift(`[${this.tag}] >`);
    }
    args.unshift(this.logTimeTag);

    this.pushLog(args);

    let methodName = logLevel.toLowerCase();
    if (logLevel === LogMethod.DEBUG) {
      methodName = 'log';
    }
    if (window.console && Logger.logLevel <= LogLevel[logLevel]) {
      console[methodName](...args);
    }
  }

  public download() {
    if (Logger.logs.length <= 0) return;

    const blob = new Blob(Logger.logs, { type: 'text/plain' });
    const objectURL = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectURL;
    anchor.download = `log-${Date.now()}.txt`;

    anchor.click();
    URL.revokeObjectURL(objectURL);
  }
}

export const logger: Logger = new Logger();

export default Logger;
